import React, { ReactNode } from 'react';
import styled from '@emotion/styled';

import { ColourPicker } from '../../../components/ColorPicker/component';
import { OL_COLOUR_ACCESSIBLE_THEME_COLOURS } from '../../../components/Style/colours';
import { Select } from '../../../components/Select/component';
import { ImageUploader } from '../../../components/ImageUploader/ImageUploader';
import { InputLabel } from '../../../components/InputLabel/component';
import { AutoSaveState } from '../../../components/AutoSaveIndicator/component';
import {
  CourseThemeData,
  CourseThemeDataFull,
} from '../../../../ReduxStorage/Courses/types';

const SettingsContainer = styled.div({
  padding: '1em 0',
});
const Settings = styled.div({
  padding: 20,
});
const Setting = styled.div({
  margin: '0 0 1.5em 0',
});

const StyledSelect = styled(Select)({
  '&.ol-select': {
    display: 'block',
    width: '100%',

    select: {
      display: 'inline-block',
      width: 'auto',
    },
  },
});

declare interface CourseAppearanceSettingsState {
  autoSaveStates: {
    'theme-color': AutoSaveState;
    'background-color': AutoSaveState;
    'background-image': AutoSaveState;
    'background-image-repeat': AutoSaveState;
  };
  errors: {
    'theme-color': string | undefined;
    'background-color': string | undefined;
    'background-image': string | undefined;
    'background-image-repeat': string | undefined;
  };
}

declare interface CourseAppearanceProps {
  path: string;
  themeData: CourseThemeDataFull;
  isUsingPortalTheme: boolean;
  currentCourseHasLoaded: boolean;
  sendUserpilotTracking: (event: string) => void;

  setCourseThemeData: ({
    path,
    themeDataChanges,
  }: {
    path: string;
    themeDataChanges: CourseThemeData;
  }) => Promise<string>;

  setCourseThemeDataPreview: ({
    themeDataChanges,
  }: {
    themeDataChanges: CourseThemeData;
  }) => void;
}

export class CourseAppearanceSettings extends React.PureComponent<
  CourseAppearanceProps,
  CourseAppearanceSettingsState
> {
  public state: CourseAppearanceSettingsState = {
    autoSaveStates: {
      'theme-color': AutoSaveState.Normal,
      'background-color': AutoSaveState.Normal,
      'background-image': AutoSaveState.Normal,
      'background-image-repeat': AutoSaveState.Normal,
    },
    errors: {
      'theme-color': '',
      'background-color': '',
      'background-image': '',
      'background-image-repeat': '',
    },
  };

  private setAutoSaveState = (
    inputName: string,
    state: AutoSaveState
  ): void => {
    this.setState((prevState: CourseAppearanceSettingsState) => ({
      autoSaveStates: { ...prevState.autoSaveStates, [inputName]: state },
    }));
  };

  private setAutoSaveError = (
    inputName: string,
    error: string | undefined
  ): void => {
    this.setState((prevState: CourseAppearanceSettingsState) => ({
      errors: { ...prevState.errors, [inputName]: error },
    }));
  };

  private handleThemeDataChange = (param: string) => (value: string | null) => {
    const { path, setCourseThemeData, sendUserpilotTracking } = this.props;
    // display saving state for input
    this.setAutoSaveState(param, AutoSaveState.Saving);

    setCourseThemeData({
      path,
      themeDataChanges: {
        [param]: value,
      },
    }).then(
      () => {
        // display saved state for input
        this.setAutoSaveState(param, AutoSaveState.Saved);
        this.setAutoSaveError(param, undefined);

        sendUserpilotTracking('ThemeColour-Updated');
      },
      (errorMessage) => {
        // return to normal state and display error
        this.setAutoSaveState(param, AutoSaveState.Normal);
        this.setAutoSaveError(param, errorMessage);
      }
    );
  };

  private handleThemeDataColorChange = (param: string) => (
    value: string | null
  ) => {
    const { setCourseThemeDataPreview } = this.props;
    setCourseThemeDataPreview({
      themeDataChanges: {
        [param]: value,
      },
    });
  };

  public render(): ReactNode {
    const {
      themeData,
      currentCourseHasLoaded,
      isUsingPortalTheme,
    } = this.props;
    const { autoSaveStates, errors } = this.state;
    const {
      'theme-color': stateThemeColor,
      'background-color': stateBackgroundColor,
      'background-image': stateBackgroundImage,
      'background-image-repeat': stateBackgroundImageRepeat,
    } = autoSaveStates;
    const {
      'theme-color': errorThemeColor,
      'background-color': errorBackgroundColor,
      'background-image': errorBackgroundImage,
      'background-image-repeat': errorBackgroundImageRepeat,
    } = errors;

    const themeColor = themeData['theme-color'];
    const backgroundImage = themeData['background-image'];
    const backgroundColor = themeData['background-color'];
    const backgroundImageRepeat = themeData['background-image-repeat'];

    return currentCourseHasLoaded ? (
      <SettingsContainer>
        <h3>Theme settings</h3>
        <Settings>
          <Setting>
            <div data-userpilot-id="course-appearance-theme">
              <ColourPicker
                label="Theme colour"
                onDrag={this.handleThemeDataColorChange('theme-color')}
                onChange={this.handleThemeDataColorChange('theme-color')}
                onClose={this.handleThemeDataChange('theme-color')}
                value={themeColor}
                presetColors={OL_COLOUR_ACCESSIBLE_THEME_COLOURS}
                autoSave
                autoSaveState={stateThemeColor}
                error={errorThemeColor}
                description={
                  isUsingPortalTheme
                    ? 'Currently using the portal brand colour as a default'
                    : null
                }
              />
            </div>
          </Setting>

          <Setting>
            <ColourPicker
              label="Background colour"
              onDrag={this.handleThemeDataColorChange('background-color')}
              onChange={this.handleThemeDataColorChange('background-color')}
              onClose={this.handleThemeDataChange('background-color')}
              value={backgroundColor}
              autoSave
              autoSaveState={stateBackgroundColor}
              error={errorBackgroundColor}
            />
          </Setting>

          <Setting>
            <InputLabel
              autoSave
              autoSaveState={stateBackgroundImage}
              error={errorBackgroundImage}
            >
              Background image
            </InputLabel>
            <ImageUploader
              imageUrl={backgroundImage}
              onChange={this.handleThemeDataChange('background-image')}
              suggestedSize={{
                width: 150,
                height: 150,
              }}
              displaySuggestedSize={false}
            />
          </Setting>

          <Setting>
            <StyledSelect
              label="Background image repeat"
              options={[
                { value: 'no-repeat', label: 'No repeat' },
                { value: 'repeat', label: 'Repeat' },
                { value: 'repeat-x', label: 'Repeat horizontally' },
                { value: 'repeat-y', label: 'Repeat vertically' },
              ]}
              value={backgroundImageRepeat}
              onValueChange={this.handleThemeDataChange(
                'background-image-repeat'
              )}
              autoSave
              autoSaveState={stateBackgroundImageRepeat}
              error={errorBackgroundImageRepeat}
            />
          </Setting>
        </Settings>
      </SettingsContainer>
    ) : null;
  }
}
